import {
  IssueDetail,
  VehicleCalendarIssues,
} from "Shared/Types/fleetmanagementapi";

export type IssueRow = Pick<
  IssueDetail,
  "id" | "summary" | "priority" | "status" | "reported_date" | "due_date"
> & { vehicleName: string; vehicleId: string };

export const issueRows = (vehicles: VehicleCalendarIssues[]): IssueRow[] => {
  const unique = new Map<string, IssueRow>();
  vehicles.forEach((vehicle) => {
    vehicle.issues.forEach((day) => {
      (day.issues || []).forEach(({ issue }) => {
        if (!unique.has(issue.id)) {
          unique.set(issue.id, {
            ...issue,
            vehicleName: vehicle.vehicle_name,
            vehicleId: vehicle.vehicle_id,
          });
        }
      });
    });
  });
  return [...unique.values()].sort(
    (a, b) =>
      b.reported_date.localeCompare(a.reported_date) ||
      a.id.localeCompare(b.id),
  );
};

export const needsIssueReview = (issue: IssueRow) =>
  ["open", "overdue", "unknown", ""].includes(issue.status);

/** Open with a past due date, or already marked overdue by the server. */
export const isOverdueIssue = (
  issue: { status?: string | null; due_date?: string | null },
  now = Date.now(),
) =>
  issue.status === "overdue" ||
  (issue.status === "open" &&
    Boolean(issue.due_date && Date.parse(issue.due_date) < now));

export const prioritizeIssues = (rows: IssueRow[], now = Date.now()) => {
  const rank = (issue: IssueRow) => {
    if (issue.status === "unknown" || !issue.status) return 4;
    if (!needsIssueReview(issue)) return 10;
    if (issue.priority === "critical") return 0;
    if (isOverdueIssue(issue, now)) return 1;
    if (issue.priority === "high") return 2;
    return 3;
  };
  return [...rows].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      b.reported_date.localeCompare(a.reported_date) ||
      a.id.localeCompare(b.id),
  );
};

export type IssueFocus = "all" | "critical" | "overdue" | "high";
/** Focus categories can overlap: a critical issue can also be overdue. */
export const filterIssueFocus = (
  rows: IssueRow[],
  focus: IssueFocus,
  now = Date.now(),
) =>
  rows.filter(
    (issue) =>
      focus === "all" ||
      (focus === "overdue"
        ? isOverdueIssue(issue, now)
        : issue.priority === focus),
  );
